import React, { useEffect, useState } from 'react';
import SlideStage from './SlideStage';
import SlideRenderer from './SlideRenderer';
import useLiveSession from '../../hooks/useLiveSession';
import usePresentation from '../../hooks/usePresentation';
import './AudienceScreen.css';

// A clean audience-only window. Notes, controls and Okiru Learn remain in the
// presenter window on the facilitator's laptop.
export default function AudienceScreen({ sessionId, presentationId }) {
  const { presentation } = usePresentation(presentationId);
  const live = useLiveSession(sessionId, null);
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);
  const [fullscreenError, setFullscreenError] = useState('');

  const enterFullscreen = async () => {
    setFullscreenError('');
    try {
      await document.documentElement.requestFullscreen?.({ navigationUI: 'hide' });
    } catch {
      setFullscreenError('Press F11 or use your browser menu to enter fullscreen.');
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'okiru-enter-fullscreen') enterFullscreen();
    };
    // This window has OS focus while presenting (it's the one in fullscreen), so a
    // clicker/keyboard's arrow keys land here, not on the presenter window. Forward
    // them to the opener, which owns slide navigation and syncs back over the socket.
    const onKey = (event) => {
      if (['ArrowRight', 'ArrowDown', 'PageDown', ' '].includes(event.key)) {
        event.preventDefault();
        window.opener?.postMessage({ type: 'okiru-nav', direction: 1 }, window.location.origin);
      } else if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(event.key)) {
        event.preventDefault();
        window.opener?.postMessage({ type: 'okiru-nav', direction: -1 }, window.location.origin);
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    window.addEventListener('message', onMessage);
    window.addEventListener('keydown', onKey);
    window.opener?.postMessage({ type: 'okiru-audience-ready' }, window.location.origin);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      window.removeEventListener('message', onMessage);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  if (!presentation) {
    return <div className="audience-screen audience-message">Unknown presentation.</div>;
  }

  if (live.ended) {
    return <div className="audience-screen audience-message" role="status">The live session has ended.</div>;
  }

  const { manifest, slides } = presentation;
  const slide = slides[live.slideIndex] || slides[0];

  return (
    <div className="audience-screen">
      {!isFullscreen && (
        <div className="audience-fullscreen-gate">
          <div className="audience-fullscreen-card">
            <span className="audience-fullscreen-kicker">Audience display ready</span>
            <strong>Start fullscreen presentation</strong>
            <p>This screen will show slides only. Facilitator notes stay on the laptop.</p>
            <button type="button" className="audience-fullscreen-btn" onClick={enterFullscreen}>Start fullscreen</button>
            {fullscreenError && <small role="alert">{fullscreenError}</small>}
          </div>
        </div>
      )}
      {!live.connected && <div className="audience-connecting" role="status">Connecting...</div>}
      <SlideStage>
        <SlideRenderer key={slide.id} slide={slide} index={live.slideIndex} total={slides.length} manifest={manifest} />
      </SlideStage>
    </div>
  );
}
